import React, { useEffect, useLayoutEffect, useState } from "react";
import Card from "../ui/Card";
import VirtualTable from "../ui/VirtualTable";
import Float from "../ui/Float";
import Input from "../ui/Input";
import Label from "../ui/Label";
import Checkbox from "../ui/Checkbox";
import Space from "../ui/Space";
import { listen } from "../hooks/Listener";
import { useTabs } from "../hooks/useTabs";
import { KBD } from "../utils/Keybindings";
import { ROUTES } from "../utils/Routes";
import { showError } from "../ui/Message";
import EnumField from "../ui/EnumField";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import MorePrintOptions from "./MorePrintOptions";
import { createModal } from "../hooks/createModal";
import { kbdEvent } from "../utils/Utils";
import SelectBox from "../ui/SelectBox";
import Table from "../ui/Table";
import { pubCall } from "../utils/PubSub";
import Button from "../ui/Button";


export default function PrintOptions(props) {
    const {
        controller = "",
        route = ROUTES.POS.PRINT,
        editRoute = ROUTES.POS.PRINT_EDIT,
        startInEdit = true,
        active = false,
        onBack = () => { },
        onComplete = () => { },
        rows = [],
        setRows = () => { },
        header = {},
        dismissOnFirstRowEscape = false,
    } = props;

    const row_key = "id";

    const [printSettings, setPrintSettings] = useState({});

    const has_customer = (header?.customer_ship_to) ? true : false;


    // Fetch printer settings on load... (move to global at some point?)
    const getPrinterSettings = () => {
        pubCall("work_station_setting.get", {}, (_s, _r) => {
            if (!_s) {
                showError(_r);
                return;
            }

            setPrintSettings(_r);

            const printerName = _r?.printer_receipt_printer?.printer_name ?? "";
            if (!printerName) return;

            setRows(prev => {
                if (!prev?.length) return prev;

                return prev.map((row, index) => {
                    if (index !== 0) return row;
                    return {
                        ...row,
                        printer_email_file: printerName,
                    };
                });
            });

            update("printer_email_file", printerName);
        });
    };

    useEffect(getPrinterSettings, []);


    const showMoreOptions = () => {
        window.bm_state.value = ROUTES.POS.PRINT_OPTIONS;
        createModal({
            title: "More Print Options",
            offset: [0, -80],
            component: <MorePrintOptions controller={controller} route={ROUTES.POS.PRINT_OPTIONS} />,
            refElement: refs.current["printer_email_file"] || refs.current["format"],
            closable: true,
            callback: (e) => {
                window.bm_state.value = ROUTES.POS.PRINT;

            }
        })

    }

    const getLabelByValue = (options = [], value = "") => {
        return options.find(item => item.value === value)?.label || "";
    };

    const format_fields = [
        { label: "Receipt", value: "R", key: KBD.R },
        { label: "Held Rct", value: "H", key: KBD.H },
        { label: "Invoice", value: "I", key: KBD.I },
        ...(has_customer ? [{ label: "Notice", value: "N", key: KBD.N }] : []),
        { label: "PickList", value: "L", key: KBD.L },
        { label: "PackSlip", value: "K", key: KBD.K },
        { label: "Manifest", value: "M", key: KBD.M },
    ];


    const copy_fields = [
        { label: "1", value: "1", key: KBD.Digit1 },
        { label: "2", value: "2", key: KBD.Digit2 },
        { label: "3", value: "3", key: KBD.Digit3 },
        { label: "4", value: "4", key: KBD.Digit4 },
        { label: "5", value: "5", key: KBD.Digit5 },
        { label: "6", value: "6", key: KBD.Digit6 },
        { label: "7", value: "7", key: KBD.Digit7 },
        { label: "8", value: "8", key: KBD.Digit8 },
        { label: "9", value: "9", key: KBD.Digit9 },
    ];

    const receipt_destination_fields = [
        { label: "RctPrnt", value: "R", key: KBD.R },
        { label: "Main", value: "M", key: KBD.M },
        { label: "Preview", value: "P", key: KBD.P },
        { label: "Email", value: "E", key: KBD.E },
        { label: "Text", value: "T", key: KBD.T },
    ];

    const invoice_destination_fields = [
        { label: "InvePrnt", value: "I", key: KBD.I },
        { label: "MainPrt", value: "M", key: KBD.M },
        { label: "Preview", value: "P", key: KBD.P },
        { label: "Email", value: "E", key: KBD.E },
    ];

    const picklist_destination_fields = [
        { label: "RctPrnt", value: "R", key: KBD.R },
        { label: "InvePrnt", value: "I", key: KBD.I },
        { label: "MainPrt", value: "M", key: KBD.M },
        { label: "Preview", value: "P", key: KBD.P },
        { label: "Webstor", value: "W", key: KBD.W },
    ];

    const email_copy_fields = [
        { label: "Send only", value: "S", key: KBD.S },
        { label: "Save only", value: "V", key: KBD.V },
        { label: "Both, send & save", value: "B", key: KBD.B },
        { label: "Edit, send & save", value: "E", key: KBD.E },
        { label: "Templates", value: "T", key: KBD.T },
    ];

    const FORMAT_STATUS = "[R]eceipt, [Held Rct], [I]nvoice, Pick[L]ist, Pac[K]Slip, [M]anifest";
    const COPY_STATUS = "Copies: 1-9";
    const EMAIL_COPY_STATUS = "[S]end only, Sa[V]e only, [B]oth send & save, [E]dit, send and save, [T]emplates";


    const getPrinterName = (printer = {}) => {
        return printer?.printer_name || "";
    };

    const getPrinterForDestination = (format, destination) => {


        if (destination === "E") {

            if (header?.customer_ship_to?.email) {
                return header?.customer_ship_to?.email
            }
        }

        if (destination === "M") {
            return getPrinterName(printSettings?.printer_main_printer);
        }

        if (destination === "R") {
            return getPrinterName(printSettings?.printer_receipt_printer);
        }

        if (destination === "I") {
            return getPrinterName(printSettings?.printer_invoice_printer);
        }



        // Email / Preview / Webstore should not auto-fill a printer
        return "";
    };


    const makeId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

    const makeNewRow = () => ({
        id: makeId(),
        format: "R",
        destination: "R",
        copies_options: "1",
        printer_email_file: "",
        __new: true,
    });


    const [selected, setSelected] = useState(rows?.[0] || {});

    const [editing, setEditing] = useState(startInEdit);
    const [align, setAlign] = useState("auto");
    const [loading, setLoading] = useState(false);

    const isNewRow = row => row?.__new === true;
    const isSelected = row => row?.[row_key] === selected?.[row_key];

    const getLastSavedRow = (_rows = rows) => {
        const saved = _rows.filter(row => !isNewRow(row));
        return saved[saved.length - 1] || {};
    };

    const makeTabs = (row = selected) => {
        const format = row?.format || "R";
        const destination = row?.destination || "E";

        const destinationDisabled = isManifestFormat(format);

        const copiesDisabled =
            isManifestFormat(format) ||
            isPreviewDestination(destination) ||
            isWebstoreDestination(destination);

        const printerDisabled =
            isPreviewDestination(destination) ||
            isWebstoreDestination(destination);

        return [
            {
                key: "format",
                label: "format",
                value: format,
                skip: false,
            },
            {
                key: "destination",
                label: "destination",
                value: destination,
                skip: destinationDisabled,
            },
            {
                key: "copies_options",
                label: "copies_options",
                value: row?.copies_options || "1",
                skip: copiesDisabled,
            },
            {
                key: "printer_email_file",
                label: "printer_email_file",
                value: row?.printer_email_file || "",
                skip: printerDisabled,
            },
        ];
    };

    const getCurrentFormat = (overrides = {}) => overrides.format ?? getVal("format") ?? selected?.format ?? "R";
    const getCurrentDestination = (overrides = {}) => overrides.destination ?? getVal("destination") ?? selected?.destination ?? "";

    const isReceiptFormat = format => ["R", "H"].includes(format);
    const isInvoiceOrPackSlipFormat = format => ["I", "K"].includes(format);
    const isPicklistFormat = format => format === "L";
    const isManifestFormat = format => format === "M";

    const isPreviewDestination = destination => destination === "P";
    const isEmailDestination = destination => destination === "E";
    const isWebstoreDestination = destination => destination === "W";
    const isPrinterDestination = destination => ["R", "M", "I"].includes(destination);

    const getDestinationOptions = (format = getCurrentFormat()) => {
        if (isManifestFormat(format)) {
            return [{ label: "X", value: "X", key: KBD.X }];
        }

        if (isReceiptFormat(format)) {
            return receipt_destination_fields;
        }

        if (isInvoiceOrPackSlipFormat(format)) {
            return invoice_destination_fields;
        }

        if (isPicklistFormat(format)) {
            return picklist_destination_fields;
        }

        return receipt_destination_fields;
    };

    const getDefaultDestination = (format = getCurrentFormat()) => {
        const options = getDestinationOptions(format);
        return options?.[0]?.value || "";
    };

    const getCopyOptions = (destination = "") => {
        if (isEmailDestination(destination)) {
            return email_copy_fields;
        }

        return copy_fields;
    };

    const isDestinationDisabled = () => {
        return isManifestFormat(getCurrentFormat());
    };

    const isCopiesDisabled = (overrides = {}) => {
        const format = getCurrentFormat(overrides);
        const destination = getCurrentDestination(overrides);

        return (
            isManifestFormat(format) ||
            isPreviewDestination(destination) ||
            isWebstoreDestination(destination)
        );
    };

    const isPrinterDisabled = (overrides = {}) => {
        const destination = getCurrentDestination(overrides);

        return (
            isPreviewDestination(destination) ||
            isWebstoreDestination(destination) ||
            isPrinterDestination(destination)
        );
    };

    const shouldSaveAfterDestination = (overrides = {}) => {
        const destination = getCurrentDestination(overrides);
        return (
            isPreviewDestination(destination) ||
            isWebstoreDestination(destination)
        );
    };

    const getStatusMessage = () => {
        const field = index;
        const format = getCurrentFormat();
        const destination = getCurrentDestination();

        if (field === "format") {
            return FORMAT_STATUS;
        }

        if (field === "destination") {
            if (isManifestFormat(format)) return "Manifest destination is X";
            if (isReceiptFormat(format)) return "[M]ain, [P]review, [E]mail";
            if (isInvoiceOrPackSlipFormat(format)) return "[I]nvePrnt, [M]ainPrt, [P]review, [E]mail";
            if (isPicklistFormat(format)) return "[I]nvePrnt, [M]ainPrt, [P]review, [W]ebstor";
        }

        if (field === "copies_options") {
            if (isEmailDestination(destination)) return EMAIL_COPY_STATUS;
            if (isCopiesDisabled()) return "Copies not required";
            return COPY_STATUS;
        }

        if (field === "printer_email_file") {

            return "Destination: Email, Phone, Filename. [PgUp] for options";
        }

        return "";
    };


    const applyFieldRules = (changedKey, changedValue) => {

        const overrides = { [changedKey]: changedValue, };

        const nextFormat = changedKey === "format" ? changedValue : getCurrentFormat(overrides);
        let nextDestination = changedKey === "destination" ? changedValue : getCurrentDestination(overrides);

        if (isManifestFormat(nextFormat)) {
            setTabs(prev => prev.map(tab => {
                if (tab.key === "destination") {
                    return { ...tab, value: "X", skip: true, };
                }
                if (tab.key === "copies_options") {
                    return { ...tab, value: "", skip: true, };
                }
                if (tab.key === "printer_email_file") {
                    return { ...tab, value: tab.value || "", skip: false, };
                }
                return {
                    ...tab, skip: false,
                };
            }));

            return;
        }

        const validDestinations = getDestinationOptions(nextFormat).map(item => item.value);

        if (!validDestinations.includes(nextDestination)) {
            nextDestination = getDefaultDestination(nextFormat);
        }

        const copiesDisabled =
            isPreviewDestination(nextDestination) ||
            isWebstoreDestination(nextDestination);

        const printerDisabled =
            isPreviewDestination(nextDestination) ||
            isWebstoreDestination(nextDestination) ||
            isPrinterDestination(nextDestination);

        const printerValue = getPrinterForDestination(nextFormat, nextDestination);

        setTabs(prev => prev.map(tab => {
            if (tab.key === "destination") {
                return {
                    ...tab,
                    value: nextDestination,
                    skip: false,
                };
            }

            if (tab.key === "copies_options") {
                if (copiesDisabled) {
                    return {
                        ...tab,
                        value: "",
                        skip: true,
                    };
                }

                if (isEmailDestination(nextDestination)) {
                    return {
                        ...tab,
                        value: "S",
                        skip: false,
                    };
                }

                return {
                    ...tab,
                    value: tab.value || "1",
                    skip: false,
                };
            }

            if (tab.key === "printer_email_file") {
                return {
                    ...tab,
                    value: printerValue,
                    skip: printerDisabled,
                };
            }

            return {
                ...tab,
                skip: false,
            };
        }));
    };


    const { tabs, setTabs, index, update, setIndex, getVal, editProps, next, prev, refs, } = useTabs(makeTabs(), "format");

    const fakeApi = (name, payload, callback) => {
        console.log(`[fake api] ${name}`, payload);
        setTimeout(() => {
            callback(true, payload);
        }, 80);
    };

    const apiCreatePrintOption = (row, callback) => {
        fakeApi("point_of_sale.createPrintOption", { row }, callback);
    };

    const apiUpdatePrintOption = (row, callback) => {
        fakeApi("point_of_sale.updatePrintOption", { row }, callback);
    };

    const apiDeletePrintOption = (row, callback) => {
        fakeApi("point_of_sale.deletePrintOption", { row }, callback);
    };

    const syncTabsToSelected = (row = selected) => {
        setTabs(makeTabs(row));
        setIndex("format");
    };



    const selectRow = (row) => {
        if (!row?.[row_key]) return;

        // Don't reset unsaved edits when clicking the same row
        if (editing && isSelected(row)) return;

        setSelected(row);
        setTabs(makeTabs(row));
        setAlign("auto");
    };

    const startEdit = (row = selected) => {
        if (!row?.[row_key]) return;

        setSelected(row);
        syncTabsToSelected(row);
        setEditing(true);


        requestAnimationFrame(() => {
            refs.current?.format?.focus?.();
            refs.current?.format?.select?.();
        });
    };

    const removeUnsavedNewRowAndJumpBack = () => {
        const updated = rows.filter(row => row[row_key] !== selected?.[row_key]);

        setRows(updated);
        setSelected(getLastSavedRow(updated));
        setAlign("bottom");
        setEditing(false);

    };



    const cancelEdit = () => {

        if (isNewRow(selected)) {
            removeUnsavedNewRowAndJumpBack();
            return;
        }

        syncTabsToSelected(selected);
        setEditing(false);

    };

    const buildCommittedRow = (overrides = {}) => ({
        ...selected,
        format: overrides.format ?? getVal("format"),
        destination: overrides.destination ?? getVal("destination"),
        copies_options: overrides.copies_options ?? getVal("copies_options"),
        printer_email_file: overrides.printer_email_file ?? getVal("printer_email_file"),
        __new: false,
    });

    const saveRow = (callback = () => { }, overrides = {}, keepEditing = false) => {

        if (!selected?.[row_key]) return;

        const committed = buildCommittedRow(overrides);

        // Valid email or phone
        if (["E", "T"].includes(committed.destination) && !committed.printer_email_file) {
            showError(
                { error: (committed.destination === "E") ? "Email address is required." : "Phone number is required." }
            );

            setIndex("printer_email_file");
            return;
        }



        const wasNew = isNewRow(selected);

        setLoading(true);

        const api = wasNew ? apiCreatePrintOption : apiUpdatePrintOption;

        api(committed, (_s, _r) => {
            setLoading(false);

            if (!_s) {
                showError(_r);
                return;
            }

            const saved = _r?.row || committed;

            setRows(prev => {
                const exists = prev.some(row => row[row_key] === saved[row_key]);

                if (!exists) {
                    return [...prev, saved];
                }

                return prev.map(row =>
                    row[row_key] === saved[row_key]
                        ? saved
                        : row
                );
            });


            setSelected(saved);
            if (!keepEditing) {
                setEditing(false);
            }

            callback(saved);
        });
    };

    const createNewRow = () => {

        if (rows.length >= 4) return;
        const row = makeNewRow();

        setRows(prev => [...prev, row]);
        setSelected(row);
        setTabs(makeTabs(row));
        setIndex("format");
        setEditing(true);
        setAlign("bottom");


        requestAnimationFrame(() => {
            refs.current?.format?.focus?.();
            refs.current?.format?.select?.();
        });
    };

    const saveAndCreateNewRow = (overrides = {}) => {
        if (loading) return;

        const canAddRow = rows.length < 4;

        saveRow(() => {
            if (canAddRow) {
                requestAnimationFrame(createNewRow);
            }
        }, overrides, canAddRow);
    };




    const removeRow = (row = selected) => {
        if (!row?.[row_key] || editing) return;

        setLoading(true);

        apiDeletePrintOption(row, (_s, _r) => {
            setLoading(false);

            if (!_s) {
                showError(_r);
                return;
            }

            const index = rows.findIndex(r => r[row_key] === row[row_key]);
            const updated = rows.filter(r => r[row_key] !== row[row_key]);

            const nextSelected = updated[index] || updated[index - 1] || updated[0] || {};

            setRows(updated);

            // Only change selection if the deleted row was selected
            if (selected?.[row_key] === row[row_key]) {
                setSelected(nextSelected);
            }
        });
    };


    const nextRow = () => {
        if (!rows.length) {
            createNewRow();
            return;
        }

        const currentIndex = rows.findIndex(row => row[row_key] === selected?.[row_key]);

        if (currentIndex === -1) {
            setSelected(rows[0]);
            return;
        }

        if (currentIndex >= rows.length - 1) {
            createNewRow();
            return;
        }

        setAlign("auto");
        setSelected(rows[currentIndex + 1]);
    };

    const prevRow = () => {
        if (!rows.length) {
            createNewRow();
            return;
        }

        const currentIndex = rows.findIndex(row => row[row_key] === selected?.[row_key]);

        if (currentIndex <= 0) {
            setSelected(rows[0]);
            return;
        }

        setAlign("auto");
        setSelected(rows[currentIndex - 1]);
    };

    const editArrowUp = () => {

        if (!rows.some(row => !isNewRow(row)) && dismissOnFirstRowEscape) {
            return;
        }

        if (isNewRow(selected)) {
            removeUnsavedNewRowAndJumpBack();
            return;
        }

        if (index === "format") {
            setEditing(false);

            return;
        }

        prev();
    };

    const editArrowDown = (overrides = {}) => {
        if (loading) return;

        const format = getCurrentFormat(overrides);
        const destination = getCurrentDestination(overrides);

        if (index === "format") {
            setIndex(isManifestFormat(format)
                ? "printer_email_file"
                : "destination"
            );
            return;
        }

        if (index === "destination") {
            if (shouldSaveAfterDestination(overrides)) {
                saveAndCreateNewRow(overrides);
                return;
            }

            setIndex(isCopiesDisabled(overrides)
                ? "printer_email_file"
                : "copies_options"
            );
            return;
        }

        if (index === "copies_options") {
            if (isEmailDestination(destination)) {
                setIndex("printer_email_file");
            } else {
                saveAndCreateNewRow(overrides);
            }
            return;
        }

        if (index === "printer_email_file") {
            saveAndCreateNewRow(overrides);
        }
    };



    useEffect(() => {
        if (!active) return;

        if (startInEdit) {
            if (!rows.length) {
                createNewRow();
            } else {
                startEdit(rows[0]);
            }
        } else {
            setEditing(false);
            setSelected(rows[0] || {});
        }
    }, [active]);

    useEffect(() => {
        if (!active) return;

        window.bm_state.value = (editing) ? ROUTES.POS.PRINT_EDIT : ROUTES.POS.PRINT
    }, [editing, active])





    useLayoutEffect(() => {
        if (!editing) return;

        requestAnimationFrame(() => {
            refs.current?.[index]?.focus?.();
            refs.current?.[index]?.select?.();
        });
    }, [index, editing]);


    const handleEscape = () => {


        if (loading) return;

        const rowIndex = rows.findIndex(
            row => row[row_key] === selected?.[row_key]
        );


        if (dismissOnFirstRowEscape && (rowIndex === 0 || !rows.length)) {
            onBack();
            return;
        }

        if (editing) {
            cancelEdit();
        } else {
            onBack();
        }
    };


    const print_nav = [

        { key: KBD.ESCAPE, action: handleEscape },
        { key: KBD.ARROW_DOWN, action: nextRow },
        { key: KBD.ARROW_UP, action: prevRow },
        { key: KBD.E, text: "Edt", action: () => startEdit() },
        { key: KBD.N, text: "New", action: createNewRow },
        { key: KBD.D, text: "Del", action: removeRow },
        { key: KBD.M, action: () => showMoreOptions() },
        { key: KBD.PAGE_DOWN, action: () => onComplete(rows.filter(item => !item?.__new), printSettings) },
    ];

    const print_edit_nav = [
        { key: KBD.ESCAPE, action: handleEscape },
        { key: KBD.ARROW_DOWN, action: editArrowDown },
        { key: KBD.ARROW_UP, action: editArrowUp },
        {
            key: KBD.PAGE_DOWN, action: () => {
                cancelEdit();
            }
        },
        { key: KBD.TAB, action: next },
        {
            key: KBD.SHIFT_TAB, action: () => {
                if (index === "format") {
                    cancelEdit()
                } else {
                    prev()
                }
            }
        },
    ];

    useEffect(() => {
        if (!active) return;
        const cleanup = listen(controller, editing ? editRoute : route, editing ? print_edit_nav : print_nav, false, [KBD.ESCAPE, KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.E, KBD.N, KBD.D, KBD.PAGE_DOWN, KBD.TAB, KBD.SHIFT_TAB]);
        return cleanup;
    }, [active, editing, selected, rows, index, tabs, loading, printSettings]);

    const columns = [
        {
            key: "format",
            title: <>Format</>,
            align: "left",
            width: 160,
            grow: true,
            render: (e, row) => {
                if (editing && isSelected(row)) {
                    return (<SelectBox openOnPageUp onClick={() => setIndex("format")} cell edit allowKeys width={150} {...editProps("format")}
                        onChange={(e) => {
                            update("format", e);
                            applyFieldRules("format", e);
                            requestAnimationFrame(() => {
                                editArrowDown({ format: e });
                            });
                        }}
                        onEnter={() => editArrowDown()}

                        options={format_fields}
                        onPageDown={(value, e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onComplete(rows.filter(item => !item?.__new), printSettings)
                        }}
                    />);
                }

                return getLabelByValue(format_fields, e);
            },
        },
        {
            key: "destination",
            title: <>Destination</>,
            align: "left",
            width: 150,
            grow: true,
            render: (e, row) => {
                if (editing && isSelected(row)) {
                    return (
                        <SelectBox
                            openOnPageUp
                            cell
                            allowKeys

                            disabled={isDestinationDisabled()}
                            width={140}
                            {...editProps("destination")}
                            edit={!isDestinationDisabled()}
                            onClick={() => setIndex("destination")}
                            onChange={(value) => {
                                update("destination", value);
                                applyFieldRules("destination", value);
                                requestAnimationFrame(() => {
                                    editArrowDown({ destination: value });
                                });
                            }}
                            onEnter={() => editArrowDown()}
                            options={getDestinationOptions()}
                        />
                    );
                }
                return getLabelByValue(getDestinationOptions(row?.format), e);
            },
        },
        {
            key: "copies_options",
            title: <>Copies / Options</>,
            align: "left",
            width: 160,
            grow: false,
            render: (e, row) => {
                if (editing && isSelected(row)) {
                    return (
                        <>
                            <SelectBox
                                openOnPageUp
                                cell
                                allowKeys

                                disabled={isCopiesDisabled()}
                                width={150}
                                {...editProps("copies_options")}
                                onClick={() => setIndex("copies_options")}
                                onChange={(value) => {
                                    update("copies_options", value);
                                    requestAnimationFrame(() => {
                                        editArrowDown({ copies_options: value });
                                    });
                                }}
                                onEnter={() => editArrowDown()}
                                edit={!isCopiesDisabled()}
                                options={getCopyOptions(getVal("destination"))}
                            />
                        </>

                    );
                }
                return Number(e)
                    ? e
                    : getLabelByValue(getCopyOptions(row?.destination), e);
            },
        },
        {
            key: "printer_email_file",
            title: <>Printer / Email / File</>,
            align: "left",
            grow: true,
            render: (e, row) => {
                if (editing && isSelected(row)) {
                    return (<Input
                        onPageUp={() => {
                            if (isManifestFormat(getVal("format"))) {
                                alert("called")
                            }
                        }}
                        disabled={isPrinterDisabled()} width={"calc(100% - 20px)"} grow onClick={(e) => e.stopPropagation()}  {...editProps("printer_email_file")} edit={!isPrinterDisabled()} onEnter={() => saveRow()} />);
                }
                return e;
            },
        },
        {
            key: "actions",
            title: <>Actions</>,
            align: "left",
            grow: false,
            width: 65,
            render: (e, row) => {
                return (editing && isSelected(row)) ? <kbd onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveRow() }} style={{ "width": "50px", "textAlign": "center" }}>done</kbd> : <kbd onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeRow(row) }} style={{ "width": "50px", "textAlign": "center", "opacity": (editing) ? "0" : "1" }}>remove</kbd>
            }

        }
    ];

    return (
        <Card
            secondary
            header={
                <Float>
                    <div style={{ color: "var(--text)", fontSize: "14px", padding: "0px 5px" }}>
                        Printing &amp; Sending Options
                    </div>
                    <small style={{ fontSize: "14px" }}>
                        {(!editing && rows?.length < 4 && <a onClick={() => kbdEvent(KBD.M)}><em>More Print Options&nbsp;&nbsp;</em></a>)}
                    </small>
                </Float>
            }
            style={{
                borderRadius: "5px",
                height: "100%",
                opacity: active ? 1 : 0.75,
            }}
        >
            <Table
                controller={controller}
                onRowClick={selectRow}
                onRowDoubleClick={() => kbdEvent(KBD.E)}
                row_key={row_key}
                current={active ? selected : {}}
                data={rows}
                columns={columns}
                width={"auto"}
                height={82}
                offset={580}
                route={editing ? editRoute : route}
                align={align}
                scrollbar={true}
                scrollindicator={true}
                infinite={false}
            />
            <div className="shim" /> <div className="shim" />


            {(editing &&
                <Status type="active"><InfoIcon />{getStatusMessage()}</Status>
            )}
            {(!editing && rows?.length < 4 &&
                <small><a onClick={() => createNewRow()}><em>&nbsp;+ add another print/send option</em></a></small>
            )}


        </Card>
    );
}